import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { sanitizeInfoHtml } from '../../helpers/sanitizeInfoHtml';
import './InformacionPreguntas.css';
import { crearErrorAction } from '../../actions/ui';
import { addRespuestasCategoria } from '../../actions/configFormularioAction';

const EMPTY_QUESTIONS = [];

// ── Componente genérico ───────────────────────────────────────────────────────
export const FormularioCat = ({ catNum, readOnly = false }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation('generic');
  const { auth, configFormulario, ui } = useSelector((s) => s);
  const { msgError, msgOk } = ui;
  const categoriaConfig = configFormulario.infoFormularioPreguntas?.categorias?.[catNum];
  const resultadoCat  = configFormulario.infoFormularioCategoria?.[`ResultatCat${catNum}`];
  const respuestasCategoria = configFormulario.infoFormularioCategoria?.RespuestasCategoria?.[catNum];
  const IdEvaluacion  = configFormulario.infoFormulario?.IdEvaluacion;

  const [formValues, setFormValues] = useState({});
  const [infoAbierta, setInfoAbierta] = useState(null);
  const handleInputChange = ({ target }) => {
    setFormValues((current) => ({ ...current, [target.name]: target.value }));
  };

  const questions = categoriaConfig?.preguntas || EMPTY_QUESTIONS;
  const programQuestions = categoriaConfig?.programas || EMPTY_QUESTIONS;
  const activeQuestions = resultadoCat == null ? questions : programQuestions;

  useEffect(() => {
    const savedAnswers = resultadoCat == null ? respuestasCategoria?.pregunta : respuestasCategoria?.programa;
    setFormValues(Object.fromEntries(activeQuestions.map((question) => [
      question.id,
      savedAnswers?.[question.orden] ?? '',
    ])));
  }, [activeQuestions, catNum, resultadoCat, respuestasCategoria]);

  const validar = (qs, label) => {
    let ok = true;
    let msg = '';
    qs.forEach((q, i) => {
      const v = formValues[q.id];
      if (v === '' || v === undefined || v === null || (q.type === 'text' && isNaN(v))) {
        ok = false;
        msg += `${t('formulario.msgError1')}${i + 1} ${t('formulario.msgError2')}\n`;
      }
    });
    if (!ok) dispatch(crearErrorAction(msg));
    return ok;
  };

  const handleEnviarCat = () => {
    if (!validar(questions)) return;
    const payload = questions.map((q) => formValues[q.id]);
    dispatch(addRespuestasCategoria(auth.tokenJwt, catNum, 'pregunta', payload, IdEvaluacion));
  };

  const handleGuardar = () => {
    const payload = Object.fromEntries(activeQuestions
      .filter((q) => formValues[q.id] !== '' && formValues[q.id] !== undefined && formValues[q.id] !== null)
      .map((q) => [q.orden, formValues[q.id]]));
    if (Object.keys(payload).length === 0) return;
    dispatch(addRespuestasCategoria(auth.tokenJwt, catNum, resultadoCat == null ? 'pregunta' : 'programa', payload, IdEvaluacion, false));
  };

  const handleEnviarPrograma = () => {
    if (!validar(programQuestions)) return;
    const payload = programQuestions.map((q) => formValues[q.id]);
    dispatch(addRespuestasCategoria(auth.tokenJwt, catNum, 'programa', payload, IdEvaluacion));
  };

  const handleEnviar    = resultadoCat == null ? handleEnviarCat : handleEnviarPrograma;
  const fase            = resultadoCat == null ? 'indicadores' : 'programas';
  const renderQuestions = (items, values, onChange, disabled = false) => items.map((q, idx) => (
    <PreguntaField
      key={q.id}
      num={idx + 1}
      question={q}
      value={values[q.id] ?? ''}
      onChange={onChange}
      disabled={disabled}
      onShowInfo={q.infoHtml?.trim() ? () => setInfoAbierta(q.infoHtml) : null}
    />
  ));

  return (
    <div className="fp-cat-panel">

      {!readOnly && (
        <div className="fp-fase-badge-wrap">
          <span className={`fp-fase-badge ${fase === 'programas' ? 'fp-fase-badge--prog' : ''}`}>
            {fase === 'indicadores' ? '📊 Indicadores' : '📋 Programas'}
          </span>
          {resultadoCat != null && (
            <span className="fp-fase-resultado">
              Resultado indicadores: <strong>{resultadoCat}</strong>
            </span>
          )}
        </div>
      )}

      {msgError && (
        <div className="ms-alert-error" style={{ marginBottom: 16 }}>{msgError}</div>
      )}
      {msgOk && (
        <div className="ms-alert-success" role="status" style={{ marginBottom: 16 }}>✓ {msgOk}</div>
      )}

      {/* Preguntas */}
      {readOnly ? (
        <>
          <div className="fp-fase-badge-wrap"><span className="fp-fase-badge">📊 Indicadores</span></div>
          <div className="fp-preguntas">
            {!categoriaConfig ? <p role="status">Cargando configuración de preguntas…</p> : renderQuestions(
              questions,
              Object.fromEntries(questions.map((q) => [q.id, respuestasCategoria?.pregunta?.[q.orden] ?? ''])),
              () => {},
              true
            )}
          </div>
          <div className="fp-fase-badge-wrap"><span className="fp-fase-badge fp-fase-badge--prog">Programas</span></div>
          <div className="fp-preguntas">
            {categoriaConfig && renderQuestions(
              programQuestions,
              Object.fromEntries(programQuestions.map((q) => [q.id, respuestasCategoria?.programa?.[q.orden] ?? ''])),
              () => {},
              true
            )}
          </div>
        </>
      ) : (
        <div className="fp-preguntas">
          {!categoriaConfig ? (
            <p role="status">Cargando configuración de preguntas…</p>
          ) : activeQuestions.length === 0 ? (
            <p role="alert">No hay preguntas configuradas para esta fase.</p>
          ) : renderQuestions(activeQuestions, formValues, handleInputChange)}
        </div>
      )}

      {/* Botón enviar */}
      {!readOnly && (
        <div className="fp-cat-footer">
          <button onClick={handleGuardar} className="u-btn-secondary" disabled={!categoriaConfig || activeQuestions.length === 0 || !activeQuestions.some((q) => formValues[q.id] !== '' && formValues[q.id] !== undefined && formValues[q.id] !== null)}>
            {t('formulario.guardar')}
          </button>
          <button onClick={handleEnviar} className="u-btn-primary" disabled={!categoriaConfig || activeQuestions.length === 0}>
            {t('formulario.enviar')}
          </button>
        </div>
      )}

      {infoAbierta !== null && (
        <div
          onClick={() => setInfoAbierta(null)}
          style={{ position:'fixed', inset:0, zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(15,23,42,0.55)' }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Información de la pregunta"
            onClick={(event) => event.stopPropagation()}
            style={{ position:'relative', width:'min(960px, 100%)', maxHeight:'90vh', overflowY:'auto', padding:'clamp(20px, 4vw, 40px)', borderRadius:8, background:'#fff', boxShadow:'0 24px 70px rgba(0,0,0,0.25)' }}
          >
            <button
              type="button"
              onClick={() => setInfoAbierta(null)}
              aria-label="Cerrar información"
              style={{ position:'absolute', top:10, right:12, border:0, background:'transparent', color:'#475569', fontSize:26, lineHeight:1, cursor:'pointer' }}
            >×</button>
            <div className="info-pregunta-content" dangerouslySetInnerHTML={{ __html: sanitizeInfoHtml(infoAbierta) }} />
          </div>
        </div>
      )}
    </div>
  );
};

// ── Campo de pregunta rediseñado ──────────────────────────────────────────────
const PreguntaField = ({ num, question, value, onChange, disabled = false, onShowInfo }) => {
  const hasIndent = question.type === 'select';
  const isEmpty = value === '' || value === undefined || value === null;
  const options = question.options || [];
  const selectedOption = options.find((option) => {
    if (String(option.value) === String(value)) return true;
    return value !== '' && Number.isFinite(Number(option.value)) && Number.isFinite(Number(value)) && Number(option.value) === Number(value);
  });
  const selectValue = selectedOption ? selectedOption.value : value;
  return (
  <div className={`fp-pregunta${hasIndent ? ' fp-pregunta--indent' : ''}`}>
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
      <label className="fp-pregunta-label" htmlFor={question.id} style={{ margin:0 }}>
        <span className="fp-pregunta-num">{num}</span>
        {question.label}
      </label>
      {onShowInfo && (
        <button
          type="button"
          onClick={onShowInfo}
          aria-label={`Información: ${question.label}`}
          title="Ver información"
          style={{ flex:'0 0 22px', width:22, height:22, padding:0, borderRadius:'50%', border:'1px solid #64748b', background:'#fff', color:'#334155', fontSize:13, fontWeight:700, cursor:'pointer' }}
        >i</button>
      )}
    </div>
    {question.optionsMissing && (
      <p role="alert" className="ms-alert-error">
        Faltan las opciones de esta pregunta en Dic_Textos. Puedes continuar con otras categorías.
      </p>
    )}
    {question.type === 'select' ? (
      <select
        id={question.id}
        name={question.id}
        value={selectValue}
        onChange={onChange}
        className={`fp-select${!disabled && isEmpty ? ' fp-field-empty' : ''}`}
        disabled={disabled || question.optionsMissing}
      >
        {value !== '' && !selectedOption && (
          <option value={value}>Valor guardado: {value}</option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    ) : (
      <input
        id={question.id}
        type="number"
        name={question.id}
        value={value}
        onChange={onChange}
        className={`fp-input${!disabled && isEmpty ? ' fp-field-empty' : ''}`}
        placeholder={disabled ? '' : '0'}
        disabled={disabled}
      />
    )}
  </div>

);
};
